import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { retry } from "@reduxjs/toolkit/query/react";
import type { AxiosError } from "axios";
import axios, { type AxiosRequestConfig } from "axios";
import { onAuthStateChanged } from "firebase/auth";

import { normalizeApiError } from "./baseQueryWithAuth.errors";
import {
  logOperationAbort,
  logOperationDegraded,
  logOperationFail,
  logOperationHandled,
  logOperationStart,
  logOperationSuccess,
} from "./baseQueryWithAuth.logging";
import {
  isAccessCheckTransportEndpoint,
  isRetryableHttpStatus,
  resolveExpectedDegradedNoiseReason,
  resolveExpectedHandledNoiseReason,
  resolveRequestTimeoutMs,
  shouldEmitApiHealthNetworkFailure,
  shouldFailFastForExpectedErrorCode,
  shouldFailFastForServerErrorEndpoint,
  shouldFailFastForTransportErrorEndpoint,
  shouldReduceAccessCheckOutageNoise,
} from "./baseQueryWithAuth.retryPolicy";
import {
  handleAiInsightsEntitlementStub,
  handleDemoAccessStub,
} from "./baseQueryWithAuth.stubs";

import { waitForFirebaseInit } from "@/auth/helloCashflowDomain/firebase";
import { adminUiCopy } from "@/copy/adminUiCopy";
import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import { API_HEALTH_EVENTS } from "@/services/health/ApiHealthEvents";
import { handleOrganisationInviteDisplayFixture } from "@/services/organisationInviteDisplayFixture";
import type { ApiError } from "@/types/api";
import { publishAdminAccessDeniedEvent } from "@/utils/adminAccessDeniedEvents";
import {
  ADMIN_E2E_BYPASS_AUTH_TOKEN,
  isAdminE2eBypassEnabled,
} from "@/utils/featureFlags";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
  readCorrelationIdFromHeaders,
} from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  getStoredPreviewEmailOverride,
  isPreviewEnvironment,
  PREVIEW_EMAIL_OVERRIDE_HEADER,
  requestPreviewEmailOverrideForMissingError,
} from "@/utils/previewEmailOverride";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "store/api/baseQueryWithAuth",
);
const AUTH_RECOVERY_WAIT_MS = 1_500;
const LOGIN_AUTH_REASON_PARAM = "auth_reason";

type LoginRedirectAuthReason = "auth_session_lost";

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const readStringField = (args: {
  source: unknown;
  key: string;
}): string | null => {
  if (!isPlainRecord(args.source)) {
    return null;
  }

  const value = args.source[args.key];
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

const isTemporaryIdentityConnectionFailure = (
  error: unknown,
  requestStarted: boolean,
): boolean => {
  if (!isPlainRecord(error)) return false;
  const code = readStringField({ source: error, key: "code" });
  const name = readStringField({ source: error, key: "name" });
  if (code === "ERR_CANCELED" || name === "CanceledError") return false;
  if (
    code &&
    [
      "auth/network-request-failed",
      "auth/timeout",
      "ERR_NETWORK",
      "ECONNABORTED",
      "ETIMEDOUT",
      "ECONNRESET",
      "ECONNREFUSED",
    ].includes(code)
  )
    return true;
  return requestStarted && !error.response && Boolean(error.request);
};

const isDefiniteIdentityTokenFailure = (error: unknown): boolean => {
  const code = readStringField({ source: error, key: "code" });
  return Boolean(
    code &&
    [
      "auth/user-token-expired",
      "auth/user-disabled",
      "auth/user-not-found",
    ].includes(code),
  );
};

const resolveRequiredPermissionFromErrorData = (
  data: unknown,
): string | null => {
  return readStringField({
    source: data,
    key: "requiredPermission",
  });
};

const resolveAccessDeniedReasonFromErrorData = (
  data: unknown,
): string | null => {
  return (
    readStringField({
      source: data,
      key: "message",
    }) ??
    readStringField({
      source: data,
      key: "detail",
    })
  );
};

const shouldPublishAdminAccessDeniedEvent = (args: {
  endpoint: string;
  statusCode: number;
  suppressAccessDeniedModal: boolean;
}): boolean => {
  // Dashboard ops panels intentionally degrade inline on 403 and must not trigger
  // the global access-denied modal that blocks role-constrained landing flows.
  if (args.endpoint.startsWith("/admin/v2/ops/")) {
    return false;
  }

  if (args.suppressAccessDeniedModal) {
    return false;
  }

  return args.statusCode === 403 && args.endpoint.startsWith("/admin/");
};

const isFormDataPayload = (value: unknown): value is FormData => {
  return typeof FormData !== "undefined" && value instanceof FormData;
};

/**
 * Hard-redirect to `/login` when we cannot recover auth in-band.
 *
 * @remarks
 * This is the last-resort recovery path for mid-session auth loss. Without it,
 * queries that hit a 401 with no current user permanently fail and the page
 * appears frozen because nothing retriggers the data load.
 *
 * Preserves the current location as a `return_url` query parameter so
 * SimpleLogin can restore the user's position after re-authentication.
 *
 * @see apps/admin/src/components/auth/SimpleProtectedRoute.tsx — buildLoginRedirectPath
 * @see apps/admin/src/utils/navigation/normalizeAdminFrontendPath.ts
 */
type RedirectTestWindow = Window & {
  __HC_ADMIN_TEST_LOGIN_REDIRECT__?: (path: string) => void;
};

const buildLoginRedirectUrl = (): string => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const params = new URLSearchParams();
  // Avoid encoding a bare "/" or "/login" as the return target.
  if (currentPath && currentPath !== "/" && !currentPath.startsWith("/login")) {
    params.set("return_url", currentPath);
  }

  return params.toString() ? `/login?${params.toString()}` : "/login";
};

const isOnLoginRoute = (): boolean =>
  window.location.pathname.startsWith("/login");

const redirectToLogin = (metadata?: {
  endpoint?: string | null;
  errorCode?: string | null;
  statusCode?: number | null;
  authReason?: LoginRedirectAuthReason | null;
}): void => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  // A 401 can also happen while the operator is already on `/login` because
  // route-level bootstrap queries still mount there. Reassigning `/login`
  // would hard-reload the same page, restart those queries, and recreate the
  // redirect loop we are trying to break.
  if (isOnLoginRoute()) {
    logger.debug(
      "API layer skipped a redundant login redirect because the operator is already on the login page",
      {
        operation: "api_redirect_to_login_skipped",
        endpoint: metadata?.endpoint ?? null,
        statusCode: metadata?.statusCode ?? null,
        errorCode: metadata?.errorCode ?? null,
        authReason: metadata?.authReason ?? null,
        currentPath,
      },
    );
    return;
  }

  const loginUrl = (() => {
    const baseUrl = new URL(buildLoginRedirectUrl(), window.location.origin);
    if (metadata?.authReason) {
      baseUrl.searchParams.set(LOGIN_AUTH_REASON_PARAM, metadata.authReason);
    }
    return `${baseUrl.pathname}${baseUrl.search}`;
  })();
  logger.warn("API layer is redirecting the operator back to login", {
    operation: "api_redirect_to_login",
    endpoint: metadata?.endpoint ?? null,
    statusCode: metadata?.statusCode ?? null,
    errorCode: metadata?.errorCode ?? null,
    authReason: metadata?.authReason ?? null,
    currentPath,
    loginUrl,
    currentUserPresent: Boolean(memoizedAuth.getCurrentUser()),
  });
  const testRedirectHandler = (window as RedirectTestWindow)
    .__HC_ADMIN_TEST_LOGIN_REDIRECT__;
  if (typeof testRedirectHandler === "function") {
    testRedirectHandler(loginUrl);
    return;
  }

  // JSDOM does not implement full document navigation; skip hard redirect in tests.
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent)) {
    return;
  }
  window.location.assign(loginUrl);
};

type RedirectToLoginMetadata = Parameters<typeof redirectToLogin>[0];

/**
 * Last-resort auth-loss recovery: clear the cached Firebase session, then
 * hard-redirect to `/login`.
 *
 * Signing out first stops Firebase restoring a stale session on reload, which
 * would make SimpleLogin navigate straight back and loop.
 */
const signOutAndRedirectToLogin = async (
  metadata: RedirectToLoginMetadata,
): Promise<void> => {
  // CRITICAL: never sign out while already on `/login`. Login-route bootstrap
  // queries can 401 and exhaust AUTH_RECOVERY_WAIT_MS while SimpleLogin's
  // one-use `?token=` sign-in (signInWithCustomToken) is still resolving
  // against Firebase. `signOut()` then clears that new session, so the page
  // stays on `/login` with no error and no signed-in request; this blocked
  // every governed Admin spoof login (RORY-049 render, Oct 2026). On `/login`
  // no redirect or reload happens, so the stale-session loop that signing out
  // prevents cannot occur.
  // https://firebase.google.com/docs/auth/web/custom-auth
  // https://firebase.google.com/docs/reference/js/auth.md#signout
  if (!isOnLoginRoute()) {
    await memoizedAuth.signOut().catch(() => undefined);
  }
  redirectToLogin(metadata);
};

/**
 * Give Firebase one short extra window to restore a persisted operator session
 * before we sign out and force `/login` (sign-out is skipped while already on
 * `/login`; see {@link signOutAndRedirectToLogin}).
 *
 * This keeps transient auth drift from bouncing operators out of the admin app
 * while still failing closed when the session is genuinely gone.
 *
 * See `docs/admin/README.md`.
 */
const waitForRecoveredAuthUser = async (args: {
  endpoint: string | null;
  correlationId: string | null;
  timeoutMs?: number;
}) => {
  const currentUser = memoizedAuth.getCurrentUser();
  if (currentUser) {
    return currentUser;
  }

  const timeoutMs = args.timeoutMs ?? AUTH_RECOVERY_WAIT_MS;
  const recoveryLogger = args.correlationId
    ? logger.withCorrelationId(args.correlationId)
    : logger;

  recoveryLogger.warn(
    "Guarded API request hit 401 without an active user; waiting briefly for auth recovery",
    {
      operation: "token_refresh_wait_for_auth_recovery",
      endpoint: args.endpoint,
      timeoutMs,
    },
  );

  return new Promise<ReturnType<typeof memoizedAuth.getCurrentUser>>(
    (resolve) => {
      let settled = false;
      let unsubscribe = () => {};

      const settle = (
        user: ReturnType<typeof memoizedAuth.getCurrentUser>,
        outcome: "recovered" | "timed_out",
      ) => {
        if (settled) {
          return;
        }
        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        recoveryLogger.debug("Auth recovery wait completed", {
          operation: "token_refresh_wait_for_auth_recovery_complete",
          endpoint: args.endpoint,
          timeoutMs,
          outcome,
          userRecovered: Boolean(user),
        });
        resolve(user);
      };

      const timeoutId = globalThis.setTimeout(() => {
        settle(memoizedAuth.getCurrentUser(), "timed_out");
      }, timeoutMs);

      unsubscribe = onAuthStateChanged(memoizedAuth.getAuth(), (user) => {
        if (!user) {
          return;
        }

        settle(user, "recovered");
      });
    },
  );
};

interface BaseQueryArgs {
  url: string;
  method?: AxiosRequestConfig["method"];
  data?: AxiosRequestConfig["data"];
  params?: AxiosRequestConfig["params"];
  // Use for background data pulls that should degrade inline without opening the global modal.
  suppressAccessDeniedModal?: boolean;
}

const getAuthHeaders = async (args: {
  correlationId: string;
  endpoint: string;
  data?: unknown;
}): Promise<{
  headers: Record<string, string>;
  hasAuthToken: boolean;
}> => {
  const token = await memoizedAuth.getIdToken();
  const hasAuthToken = typeof token === "string" && token.length > 0;
  const shouldUseE2eBypassToken = !hasAuthToken && isAdminE2eBypassEnabled();
  const authHeaderLogger = logger.withCorrelationId(args.correlationId);

  const headers: Record<string, string> = {
    [CORRELATION_ID_HEADER]: args.correlationId,
  };

  // Let Axios set the multipart boundary for browser-managed FormData uploads.
  if (!isFormDataPayload(args.data)) {
    headers["Content-Type"] = "application/json";
  }

  if (isPreviewEnvironment()) {
    const previewEmailOverride = getStoredPreviewEmailOverride();
    if (previewEmailOverride) {
      headers[PREVIEW_EMAIL_OVERRIDE_HEADER] = previewEmailOverride;
    }
  }

  if (hasAuthToken || shouldUseE2eBypassToken) {
    headers.Authorization = `Bearer ${
      hasAuthToken ? token : ADMIN_E2E_BYPASS_AUTH_TOKEN
    }`;

    if (shouldUseE2eBypassToken) {
      authHeaderLogger.debug(
        "Using localhost E2E bypass token for guarded API request",
        {
          operation: "api_auth_token_e2e_bypass",
          endpoint: args.endpoint,
        },
      );
    }
  } else {
    const logMeta = {
      operation: "api_auth_token_missing",
      endpoint: args.endpoint,
    };
    if (isAccessCheckTransportEndpoint(args.endpoint)) {
      authHeaderLogger.debug(
        "No auth token present for access-check request",
        logMeta,
      );
    } else {
      authHeaderLogger.warn(
        "No auth token present for guarded API request",
        logMeta,
      );
    }
  }

  return {
    headers,
    hasAuthToken:
      (typeof token === "string" && token.trim().length > 0) ||
      shouldUseE2eBypassToken,
  };
};

export const baseQueryWithAuth: BaseQueryFn<
  BaseQueryArgs,
  unknown,
  ApiError
> = async (
  { url, method = "GET", data, params, suppressAccessDeniedModal = false },
  { signal },
) => {
  const displayFixtureResult = handleOrganisationInviteDisplayFixture({
    url,
    method,
    data,
    params,
  });
  if (displayFixtureResult) return displayFixtureResult;

  const normalizedUrl = `/${url.replace(/^\/+/, "").replace(/\/+$/, "")}`;
  const normalizedMethod = method.toString().toUpperCase();
  const normalizedParams =
    typeof params === "object" && params !== null
      ? (params as Record<string, unknown>)
      : {};

  const aiInsightsStubResult = await handleAiInsightsEntitlementStub({
    normalizedMethod,
    normalizedUrl,
    normalizedParams,
    data,
  });
  if (aiInsightsStubResult) {
    return aiInsightsStubResult;
  }

  const demoAccessStubResult = await handleDemoAccessStub({
    normalizedMethod,
    normalizedUrl,
    normalizedParams,
    data,
  });
  if (demoAccessStubResult) {
    return demoAccessStubResult;
  }

  const fullUrl = `${import.meta.env.VITE_API_URL}${url}`;
  const requestStartTime = Date.now();
  const correlationId = generateCorrelationId();
  const requestLogger = logger.withCorrelationId(correlationId);
  const endpoint = normalizedUrl;
  let requestCompleted = false;
  let requestStarted = false;

  try {
    // Firebase can restore a persisted session asynchronously on first load. Hold guarded API
    // calls until that readiness lifecycle completes so we do not emit pre-auth 401s during
    // bootstrap and accidentally trigger route recovery logic.
    await waitForFirebaseInit();

    const { headers, hasAuthToken } = await getAuthHeaders({
      correlationId,
      endpoint,
      data,
    });

    if (!hasAuthToken) {
      requestLogger.warn("Skipping guarded API request without auth token", {
        operation: "api_auth_token_missing_short_circuit",
        endpoint,
        method: normalizedMethod,
      });
      requestCompleted = true;

      return {
        error: {
          status: 401,
          data: {
            errorCode: "AUTH_TOKEN_MISSING",
            message: adminUiCopy.authLogin.authSessionLost,
            correlationId,
          },
          message: adminUiCopy.authLogin.authSessionLost,
          code: "unauthorized",
          correlationId,
        },
      };
    }

    logOperationStart(requestLogger, {
      endpoint,
      method: normalizedMethod,
      correlationId,
    });

    requestStarted = true;
    const result = await axios(fullUrl, {
      method,
      data,
      params,
      signal,
      headers,
      timeout: resolveRequestTimeoutMs(url),
    });

    const requestDuration = Date.now() - requestStartTime;
    const responseCorrelationId = readCorrelationIdFromHeaders(result.headers);
    const resolvedCorrelationId = responseCorrelationId ?? correlationId;
    if (!responseCorrelationId) {
      requestLogger.debug(
        "API response missing correlation ID; using generated request correlation",
        {
          operation: "api_correlation_id_fallback",
          endpoint,
          method: normalizedMethod,
        },
      );
    }

    logOperationSuccess(requestLogger, {
      endpoint,
      method: normalizedMethod,
      status: result.status,
      durationMs: requestDuration,
      correlationId: resolvedCorrelationId,
    });
    requestCompleted = true;

    return { data: result.data };
  } catch (axiosError) {
    const err = (isPlainRecord(axiosError)
      ? axiosError
      : {}) as unknown as AxiosError;
    const requestDuration = Date.now() - requestStartTime;
    const safeFallbackMessage = "Request failed";
    const isIdentityRead =
      endpoint === "/admin/v2/me" && normalizedMethod === "GET";
    const responseCorrelationId = readCorrelationIdFromHeaders(
      err.response?.headers,
    );
    // CRITICAL: identity error metadata is untrusted. Use the locally
    // generated reference if the response header is not a safe identifier.
    // https://cheatsheetseries.owasp.org/cheatsheets/Error_Handling_Cheat_Sheet.html
    const unsafeIdentityReference = Boolean(
      isIdentityRead &&
      responseCorrelationId &&
      !/^[A-Za-z0-9._:-]{1,128}$/.test(responseCorrelationId),
    );
    const resolvedCorrelationId = unsafeIdentityReference
      ? correlationId
      : (responseCorrelationId ?? correlationId);
    const mappedError = normalizeApiError({
      axiosError: err,
      fallbackMessage: safeFallbackMessage,
      ...(unsafeIdentityReference
        ? { correlationIdOverride: resolvedCorrelationId }
        : {}),
    });
    // CRITICAL PD-51: only a genuine connection failure is transient. An
    // unknown local exception or token revocation cannot grant stale access.
    // https://firebase.google.com/docs/auth/web/manage-users#get_a_users_profile
    const revoked =
      isIdentityRead && !err.response && isDefiniteIdentityTokenFailure(err);
    const identityLocalFailure =
      isIdentityRead &&
      !err.response &&
      (revoked || !isTemporaryIdentityConnectionFailure(err, requestStarted));
    const normalizedError: ApiError = identityLocalFailure
      ? {
          ...mappedError,
          status: revoked ? 401 : 0,
          data: null,
          code: revoked ? "AUTH_SESSION_INVALID" : "ACCESS_CHECK_FAILED",
          ...(revoked
            ? { message: adminUiCopy.authLogin.authSessionLost }
            : {}),
        }
      : mappedError;
    requestPreviewEmailOverrideForMissingError(normalizedError);
    const safeError = new Error(normalizedError.message);
    const errorCode = err.code;
    const errorName = err.name || "AxiosError";
    const errorStack = err.stack;
    const hasHttpResponse = Boolean(err.response);
    const isOffline =
      typeof navigator !== "undefined" ? !navigator.onLine : false;
    safeError.name = isIdentityRead ? "IdentityCheckError" : errorName;
    safeError.stack = isIdentityRead ? undefined : errorStack;
    const statusCode = normalizedError.status;

    if (errorCode === "ERR_CANCELED" || errorName === "CanceledError") {
      logOperationAbort(requestLogger, {
        endpoint,
        method: normalizedMethod,
        durationMs: requestDuration,
        correlationId: resolvedCorrelationId,
        extraMeta: {
          reason: "REQUEST_ABORTED",
          offline: isOffline,
        },
      });

      return {
        error: isIdentityRead
          ? {
              status: 0,
              data: null,
              message: "Identity check was interrupted.",
              code: "ACCESS_CHECK_FAILED",
              correlationId: resolvedCorrelationId,
            }
          : {
              // 499 is a commonly-used "client closed request" status.
              status: 499,
              data: null,
              message: "Request aborted",
            },
      };
    }

    if (
      typeof statusCode === "number" &&
      shouldPublishAdminAccessDeniedEvent({
        endpoint,
        statusCode,
        suppressAccessDeniedModal,
      })
    ) {
      const accessDeniedErrorCode = readStringField({
        source: normalizedError.data,
        key: "errorCode",
      });
      publishAdminAccessDeniedEvent({
        endpoint,
        status: statusCode,
        correlationId: resolvedCorrelationId,
        requiredPermission: resolveRequiredPermissionFromErrorData(
          normalizedError.data,
        ),
        reason: resolveAccessDeniedReasonFromErrorData(normalizedError.data),
        ...(accessDeniedErrorCode ? { errorCode: accessDeniedErrorCode } : {}),
      });
    }

    const commonFailureMeta = {
      offline: isOffline,
      ...(errorCode ? { axiosCode: errorCode } : {}),
      ...(normalizedError.code
        ? { publicErrorCode: normalizedError.code }
        : {}),
      isAxiosError: axios.isAxiosError(axiosError),
    };

    if (
      shouldReduceAccessCheckOutageNoise({
        endpoint,
        hasHttpResponse,
        errorCode,
        errorMessage: normalizedError.message,
        isOffline,
      })
    ) {
      logOperationDegraded(requestLogger, {
        endpoint,
        method: normalizedMethod,
        status: statusCode,
        durationMs: requestDuration,
        correlationId: resolvedCorrelationId,
        extraMeta: {
          ...commonFailureMeta,
          reason: "ACCESS_CHECK_TRANSPORT_UNAVAILABLE",
          noiseReduced: true,
        },
      });
    } else {
      const expectedHandledReason = resolveExpectedHandledNoiseReason({
        endpoint,
        statusCode,
        publicErrorCode: normalizedError.code,
      });
      const expectedDegradedReason = resolveExpectedDegradedNoiseReason({
        endpoint,
        statusCode,
        publicErrorCode: normalizedError.code,
      });

      if (expectedHandledReason) {
        logOperationHandled(requestLogger, {
          endpoint,
          method: normalizedMethod,
          status: statusCode,
          durationMs: requestDuration,
          correlationId: resolvedCorrelationId,
          extraMeta: {
            ...commonFailureMeta,
            reason: expectedHandledReason,
            noiseReduced: true,
          },
        });
      } else if (expectedDegradedReason) {
        logOperationDegraded(requestLogger, {
          endpoint,
          method: normalizedMethod,
          status: statusCode,
          durationMs: requestDuration,
          correlationId: resolvedCorrelationId,
          extraMeta: {
            ...commonFailureMeta,
            reason: expectedDegradedReason,
            noiseReduced: true,
          },
        });
      } else {
        logOperationFail(requestLogger, {
          endpoint,
          method: normalizedMethod,
          error: safeError,
          status: statusCode,
          durationMs: requestDuration,
          correlationId: resolvedCorrelationId,
          extraMeta: commonFailureMeta,
        });
      }
    }

    if (
      shouldEmitApiHealthNetworkFailure({
        hasHttpResponse,
        errorCode,
        errorMessage: normalizedError.message,
        isOffline,
      })
    ) {
      requestLogger.debug(
        "Backend transport failure detected; notifying admin API health gate",
        {
          operation: "api_health_gate_network_failure_signal",
          endpoint,
          method: normalizedMethod,
          status: statusCode,
          offline: isOffline,
          axiosCode: errorCode ?? null,
        },
      );
      API_HEALTH_EVENTS.emitNetworkFailure({
        url: endpoint,
        method: normalizedMethod,
        status: typeof statusCode === "number" ? statusCode : undefined,
        message: normalizedError.message,
      });
    }

    return {
      error: isIdentityRead
        ? { ...normalizedError, correlationId: resolvedCorrelationId }
        : normalizedError,
    };
  } finally {
    if (!requestCompleted) {
      requestLogger.debug("API request finished without success response", {
        operation: "api_request_terminal_transition",
        endpoint,
        method: normalizedMethod,
      });
    }
  }
};

/**
 * Shared admin RTK Query wrapper for auth recovery, fail-fast policies, and bounded retries.
 *
 * @remarks
 * This wrapper gives each request one in-band 401 recovery attempt via Firebase
 * token refresh, then applies endpoint-specific fail-fast rules so guarded
 * workspaces can surface explicit recovery UI instead of spinning through
 * generic retries.
 *
 * If auth recovery cannot succeed because there is no active Firebase user or
 * refresh itself fails definitively, the wrapper signs out and redirects to `/login`
 * via {@link signOutAndRedirectToLogin} so the operator does not get stuck on a
 * dead page after mid-session auth loss. While already on `/login` it does
 * neither, so an in-flight one-use token sign-in is not cleared.
 */
export const enhancedBaseQuery = retry(
  async (args, api, extraOptions) => {
    let result = await baseQueryWithAuth(args, api, extraOptions);
    const isMutation = api.type === "mutation";

    // Handle 401 errors with token refresh
    if (result.error?.status === 401) {
      const originalError = result.error;
      let refreshUserUid: string | null = null;
      try {
        // Force token refresh
        const endpoint = typeof api.endpoint === "string" ? api.endpoint : null;
        const hadActiveUserBeforeRecovery = Boolean(
          memoizedAuth.getCurrentUser(),
        );
        let user = memoizedAuth.getCurrentUser();
        if (user) {
          logger.debug("Forcing auth token refresh after 401 response", {
            operation: "token_refresh_forced",
            endpoint,
            reason: "unauthorized_response",
          });
        } else {
          user = await waitForRecoveredAuthUser({
            endpoint,
            correlationId: result.error.correlationId ?? null,
          });
        }

        if (user) {
          logger.debug(
            "Auth recovery located an active user; retrying the guarded request",
            {
              operation: "token_refresh_retry_after_recovery",
              endpoint,
              recoveredAfterTransientLoss: !hadActiveUserBeforeRecovery,
            },
          );
          refreshUserUid = user.uid;
          await user.getIdToken(true);
          // Retry the original request
          result = await baseQueryWithAuth(args, api, extraOptions);
        } else {
          logger.warn(
            "Auth loss detected without active user; redirecting to login",
            {
              operation: "token_refresh_user_missing",
              endpoint,
            },
          );
          await signOutAndRedirectToLogin({
            endpoint,
            errorCode: "token_refresh_user_missing",
            statusCode: 401,
            authReason: "auth_session_lost",
          });
        }
      } catch (error) {
        const identityRead =
          !isMutation &&
          api.endpoint === "getAdminV2Me" &&
          args.url.replace(/^\/+|\/+$/g, "") === "admin/v2/me" &&
          (args.method ?? "GET").toString().toUpperCase() === "GET";
        const activeUserUid = memoizedAuth.getCurrentUser()?.uid;
        const sameUser = Boolean(
          refreshUserUid && activeUserUid === refreshUserUid,
        );
        const confirmedDenialCodes = new Set([
          "AUTH_TOKEN_MISSING",
          "AUTH_SESSION_INVALID",
          "AUTH_ACCOUNT_DISABLED",
          "AUTH_SESSION_REVOKED",
          "AUTH/USER-TOKEN-EXPIRED",
          "AUTH/USER-DISABLED",
          "AUTH/USER-NOT-FOUND",
        ]);
        const explicitCode = readStringField({
          source: originalError.data,
          key: "errorCode",
        });
        const confirmedDenial =
          [
            originalError.code,
            readStringField({ source: originalError.data, key: "code" }),
            explicitCode,
          ].some(
            (code) =>
              typeof code === "string" &&
              confirmedDenialCodes.has(code.toUpperCase()),
          ) ||
          Boolean(
            explicitCode &&
            ["UNAUTHORIZED", "FORBIDDEN", "PERMISSION_DENIED"].includes(
              explicitCode.toUpperCase(),
            ),
          );

        // CRITICAL PD-51: retain only the same actor after a temporary network
        // failure during an identity refresh. A stale or cancelled request must
        // never sign out a new actor; confirmed denials still fail closed.
        // https://firebase.google.com/docs/auth/web/manage-users#get_a_users_profile
        if (
          identityRead &&
          (api.signal.aborted || (refreshUserUid && !sameUser))
        ) {
          logger.debug("Stale identity refresh was discarded", {
            operation: "identity_token_refresh_discarded",
          });
        } else if (
          identityRead &&
          sameUser &&
          readStringField({ source: error, key: "code" }) ===
            "auth/network-request-failed" &&
          !confirmedDenial
        ) {
          logger.warn("Identity token refresh temporarily unavailable", {
            operation: "identity_token_refresh_unavailable",
            correlationId: originalError.correlationId,
          });
          result = {
            error: {
              status: 503,
              code: "ACCESS_CHECK_FAILED",
              data: null,
              message: "Request failed",
              correlationId: originalError.correlationId,
            },
          };
        } else {
          logger.error(
            "Token refresh failed; redirecting to login",
            !identityRead && error instanceof Error ? error : undefined,
            { operation: "token_refresh_failed" },
          );
          logger.warn("Token refresh failed; redirecting to login", {
            operation: "token_refresh_failed",
          });
          await signOutAndRedirectToLogin({
            endpoint: typeof api.endpoint === "string" ? api.endpoint : null,
            errorCode: "token_refresh_failed",
            statusCode: 401,
            authReason: "auth_session_lost",
          });
        }
      }
    }

    // Stop retries early for non-retryable HTTP statuses.
    if (result.error) {
      const status = result.error.status;
      const endpoint = typeof api.endpoint === "string" ? api.endpoint : null;
      const normalizedErrorCode =
        typeof result.error.code === "string" ? result.error.code : null;
      if (typeof status === "number") {
        logger.debug("API request returned non-success status", {
          operation: "api_non_success_response",
          endpoint: endpoint ?? null,
          status,
          retryEligible: isRetryableHttpStatus(status),
        });
      }
      // Mutations are state-changing operations. Keep retries bounded to the
      // explicit one-time 401 refresh replay above to avoid duplicate writes.
      if (isMutation) {
        logger.debug("Retry exhausted for mutation request", {
          operation: "api_retry_exhausted",
          endpoint: endpoint ?? null,
          status,
          reason: "mutation_fail_fast",
        });
        return retry.fail(result.error);
      }

      if (
        shouldFailFastForExpectedErrorCode({
          endpoint,
          errorCode: normalizedErrorCode,
        })
      ) {
        logger.debug("Retry exhausted due to expected fail-fast API error", {
          operation: "api_retry_exhausted",
          endpoint,
          status,
          reason: "expected_error_code_fail_fast",
          errorCode: normalizedErrorCode,
        });
        return retry.fail(result.error);
      }

      // Revenue subscriptions 500s are deterministic service failures; fail fast and
      // rely on the operator-triggered refresh action instead of automatic retry loops.
      if (shouldFailFastForServerErrorEndpoint({ endpoint, status })) {
        logger.debug("Retry exhausted due to fail-fast server error policy", {
          operation: "api_retry_exhausted",
          endpoint,
          status,
          reason: "server_error_fail_fast_endpoint",
        });
        return retry.fail(result.error);
      }
      if (shouldFailFastForTransportErrorEndpoint(endpoint)) {
        logger.debug(
          "Retry exhausted due to transport fail-fast endpoint policy",
          {
            operation: "api_retry_exhausted",
            endpoint,
            status,
            reason: "transport_fail_fast_endpoint",
          },
        );
        return retry.fail(result.error);
      }

      if (
        status === 401 ||
        status === 499 ||
        (typeof status === "number" && !isRetryableHttpStatus(status))
      ) {
        logger.debug("Retry exhausted for non-retryable API response", {
          operation: "api_retry_exhausted",
          endpoint: endpoint ?? null,
          status,
          reason: "non_retryable_status",
        });
        return retry.fail(result.error);
      }
    }

    return result;
  },
  {
    maxRetries: 3,
    backoff: async (attempt) => {
      // Keep retries bounded for operator routes so permission-gated workspaces
      // fail fast and can surface actionable recovery UI.
      const delay = 1000 * attempt;
      logger.debug("API retry backoff", {
        operation: "api_retry_backoff",
        attempt,
        delayMs: delay,
      });
      await new Promise((resolve) => setTimeout(resolve, delay));
    },
  },
);

export { normalizeApiError };
